import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle2, Send } from 'lucide-react';
import { API_BASE } from '@/lib/api-base';
import { useStorefront, type StorefrontFormField } from '@/context/StorefrontContext';
import { useAuth } from '@/context/AuthContext';

function inputType(field: StorefrontFormField): string {
  if (field.type === 'number') return 'number';
  if (field.type === 'date') return 'date';
  if (field.type === 'email') return 'email';
  return 'text';
}

export function StorefrontFormPage() {
  const location = useLocation();
  const { host, routes, forms } = useStorefront();
  const { accessToken, user } = useAuth();
  const route = routes.find((item) => item.routePath === location.pathname);
  const form = forms.find((item) => item.formKey === route?.targetKey);
  const fields = useMemo(
    () => (Array.isArray(form?.schemaJson.fields) ? form?.schemaJson.fields : []) as StorefrontFormField[],
    [form],
  );
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ submissionId: string; referenceNo: string | null } | null>(null);

  useEffect(() => {
    setValues((current) => ({
      ...current,
      contactName: current.contactName ?? user?.name ?? '',
      contactEmail: current.contactEmail ?? user?.email ?? '',
    }));
  }, [user?.email, user?.name]);

  if (!route || !form) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-slate-900 mb-3">Form not available</h1>
        <p className="text-slate-500">This storefront form has not been published yet.</p>
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-3">{form.title} submitted</h1>
        <p className="text-slate-500 mb-2">Submission ID: <span className="font-mono">{result.submissionId}</span></p>
        {result.referenceNo && (
          <p className="text-slate-500">Reference: <span className="font-mono font-semibold text-brand-700">{result.referenceNo}</span></p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12 animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">{form.title}</h1>
        {form.description && <p className="text-slate-500">{form.description}</p>}
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setSubmitting(true);
          setError(null);
          try {
            const payload: Record<string, unknown> = {};
            for (const field of fields) {
              const value = values[field.name]?.trim() ?? '';
              if (value) payload[field.name] = field.type === 'number' ? Number(value) : value;
            }
            const res = await fetch(`${API_BASE}/api/storefront/forms/${encodeURIComponent(form.formKey)}/submit`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              },
              body: JSON.stringify({
                host,
                routePath: location.pathname,
                payload,
              }),
            });
            const body = await res.json() as {
              ok: boolean;
              data?: { submissionId: string; referenceNo: string | null };
              error?: { message?: string };
            };
            if (!res.ok || !body.ok || !body.data) throw new Error(body.error?.message ?? 'Submission failed');
            setResult(body.data);
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Submission failed');
          } finally {
            setSubmitting(false);
          }
        }}
        className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5"
      >
        <div className="grid md:grid-cols-2 gap-4">
          {fields.map((field) => (
            <label
              key={field.name}
              className={`block ${field.type === 'textarea' ? 'md:col-span-2' : ''}`}
            >
              <span className="block text-xs font-medium text-slate-500 mb-1.5">
                {field.label ?? field.name}
                {field.required ? ' *' : ''}
              </span>
              {field.type === 'textarea' ? (
                <textarea
                  rows={5}
                  required={Boolean(field.required)}
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValues((current) => ({ ...current, [field.name]: e.target.value }))}
                  placeholder={field.placeholder ?? ''}
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm focus:outline-none focus:border-brand-400 resize-none"
                />
              ) : field.type === 'select' ? (
                <select
                  required={Boolean(field.required)}
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValues((current) => ({ ...current, [field.name]: e.target.value }))}
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm focus:outline-none focus:border-brand-400 bg-white"
                >
                  <option value="">Select...</option>
                  {(field.options ?? []).map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={inputType(field)}
                  required={Boolean(field.required)}
                  value={values[field.name] ?? ''}
                  onChange={(e) => setValues((current) => ({ ...current, [field.name]: e.target.value }))}
                  placeholder={field.placeholder ?? ''}
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-3 text-sm focus:outline-none focus:border-brand-400"
                />
              )}
            </label>
          ))}
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-700 px-5 py-3 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-60 transition-colors"
        >
          <Send className="w-4 h-4" />
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </form>
    </div>
  );
}
